import React, { useState, useEffect } from "react";
import { Modal } from "../../components/ui/modal";
import Input from "../../components/form/input/InputField";
import axios from "axios";
import { CheckCircle, CalendarDays } from "lucide-react";
import ListboxComponent from "../../components/common/ListboxComponent";
import {
  modalStyles,
  modalTitle,
  modalDescription,
  formStyles,
  label,
  input,
  errorMessage,
  closeButton,
  saveButton,
  saveButtonDisabled,
  successModalStyles,
  successModalCloseButton,
  successModalIconContainer,
  successModalHeading,
  successModalButton,
} from "../../styles/components/UserProfile/UserInfoCard.styles";

type MeetingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  state: {
    lead_id: string;
  };
  onMeetingCreated: () => void;
};

const statusOptions = [
  { id: "planned", name: "Planned" },
  { id: "held", name: "Held" },
  { id: "not held", name: "Not Held" },
];

const getFormattedDateTime = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const MeetingModal: React.FC<MeetingModalProps> = ({
  isOpen,
  onClose,
  state,
  onMeetingCreated,
}) => {
  const { lead_id } = state;

  const [meeting_date, setMeetingDate] = useState("");
  const [location, setLocation] = useState("");
  const [agenda, setAgenda] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [minDate, setMinDate] = useState("");
  const [errors, setErrors] = useState<{
    meeting_date?: string;
    location?: string;
    agenda?: string;
    status?: string;
  }>({});

  useEffect(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + now.getTimezoneOffset() + 330); // Adjust to IST
    setMinDate(getFormattedDateTime(now));
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof typeof errors
  ) => {
    const value = e.target.value;
    switch (field) {
      case "meeting_date":
        setMeetingDate(value);
        break;
      case "location":
        setLocation(value);
        break;
      case "agenda":
        setAgenda(value);
        break;
    }
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleStatusChange = (selected: { id: string; name: string } | null) => {
    const val = selected?.id || "";
    setStatus(val);
    setErrors((prev) => ({
      ...prev,
      status: val ? undefined : "Status field is required",
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const newErrors: typeof errors = {};

    if (!meeting_date) newErrors.meeting_date = "Meeting date & time field is required";

    if (meeting_date) {
      const selectedDate = new Date(meeting_date);

      const now = new Date();
      now.setMinutes(now.getMinutes() + now.getTimezoneOffset() + 330); // Convert to IST

      // Compare only up to minutes
      const selectedTime = new Date(selectedDate);
      selectedTime.setSeconds(0, 0);

      const currentTime = new Date(now);
      currentTime.setSeconds(0, 0);

      if (selectedTime < currentTime) {
        newErrors.meeting_date = "This date or time is expired. Please select a current or future date and  time.";
      }
    }


    if (!location.trim()) newErrors.location = "Location field is required";
    if (!agenda.trim()) newErrors.agenda = "Agenda field is required";
    if (!status) newErrors.status = "Status field is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("lead_id", lead_id.toString());
    formData.append("location", location);
    formData.append("agenda", agenda);
    formData.append("status", status);

    const dateObj = new Date(meeting_date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const hours = String(dateObj.getHours()).padStart(2, "0");
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");
    const seconds = "00";
    const formattedMeetingDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    formData.append("meeting_date", formattedMeetingDate);

    const token = sessionStorage.getItem("access_token");
    if (!token) {
      setErrors({ ...newErrors, agenda: "Authentication error! Please log in." });
      setSaving(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/meetings/create`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setMeetingDate("");
        setLocation("");
        setAgenda("");
        setStatus("");
        onClose();
        setTimeout(() => {
          setSuccessModalOpen(false);
          if (onMeetingCreated) onMeetingCreated();
        }, 2000);
      } else {
        setErrors({
          ...newErrors,
          agenda: response.data?.message || "Unexpected server response",
        });
      }
    } catch (error: any) {
      console.error("API Error Details:", {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
      });
      setErrors({
        ...newErrors,
        agenda: error?.response?.data?.message || "Failed to create meeting. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
    onMeetingCreated();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} className={modalStyles}>
        <h4 className={modalTitle}>Schedule Meeting</h4>
        <p className={modalDescription}>
          Fill in the details to schedule a new meeting.
        </p>
        <form onSubmit={handleSubmit} noValidate className={formStyles}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative">
              <label className={label}>
                Select Date & Time <span className="text-error-500 dark:text-red-400">*</span>
              </label>
              <div className="relative">
                <Input
                  type="datetime-local"
                  value={meeting_date}
                  onChange={(e) => handleChange(e, "meeting_date")}
                  min={minDate}
                  max="2099-12-31T23:59"
                  className={`${input} ${errors.meeting_date
                    ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                    : ""
                    }`}
                />
                <CalendarDays
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 dark:text-white w-5 h-5 pointer-events-none"
                />
              </div>
              {errors.meeting_date && (
                <p className={errorMessage}>{errors.meeting_date}</p>
              )}
            </div>
            <div className="relative">
              <ListboxComponent
                value={statusOptions.find((opt) => opt.id === status) || null}
                onChange={handleStatusChange}
                items={statusOptions}
                label="Status"
                displayKey="name"
                required
                error={errors.status}
                className={`${input} ${errors.status
                  ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                  : ""
                  }`}
              />
            </div>
          </div>
          <div className="relative">
            <label className={label}>
              Location <span className="text-error-500 dark:text-red-400">*</span>
            </label>
            <Input
              type="text"
              value={location}
              onChange={(e) => handleChange(e, "location")}
              className={`${input} ${errors.location
                ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                : ""
                }`}
            />
            {errors.location && <p className={errorMessage}>{errors.location}</p>}
          </div>
          <div className="relative">
            <label className={label}>
              Agenda <span className="text-error-500 dark:text-red-400">*</span>
            </label>
            <textarea
              value={agenda}
              onChange={(e) => handleChange(e, "agenda")}
              rows={4}
              className={`${input} min-h-[5rem] ${errors.agenda
                ? "border-red-400 ring-2 ring-red-500/10 dark:border-red-400 dark:ring-2 dark:ring-red-500/10"
                : ""
                }`}
            />
            {errors.agenda && <p className={errorMessage}>{errors.agenda}</p>}
          </div>
          <div className="flex flex-col sm:flex-row gap-6 mt-4 items-center">
            <div className="flex-1 flex gap-4 justify-center sm:justify-end">
              <button
                type="submit"
                disabled={saving}
                className={`${saveButton} ${saving ? saveButtonDisabled : ""}`}
              >
                {saving ? "Saving..." : "Submit"}
              </button>
              <button type="button" onClick={onClose} className={closeButton}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        className={successModalStyles}
      >
        <button
          onClick={handleSuccessModalClose}
          className={successModalCloseButton}
        />
        <div className="flex justify-center">
          <div className={successModalIconContainer}>
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className={successModalHeading}>Meeting Created Successfully</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          The meeting has been added to the system successfully.
        </p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleSuccessModalClose}
            className={successModalButton}
          >
            OK
          </button>
        </div>
      </Modal>
    </>
  );
};

export default MeetingModal;